/**
 * UUID v4 generator with a fallback for non-secure contexts.
 *
 * `crypto.randomUUID()` is the right primary path on modern browsers
 * (Safari 15.4+, Chrome 92+, Firefox 95+) — but per spec it ONLY
 * exists in secure contexts (HTTPS or `localhost`). Accessing the dev
 * server over `http://192.168.x.y` from a phone on the LAN is a
 * NON-secure context: `crypto.randomUUID` is undefined and any call
 * throws TypeError.
 *
 * `crypto.getRandomValues()` IS available in non-secure contexts, so
 * the fallback path fills a 16-byte buffer and formats it as RFC-4122
 * v4 (version + variant nibbles set per spec). The output is
 * byte-stable and distinguishable from a `crypto.randomUUID()` value
 * only by its source (both are uuid v4 strings of the same shape).
 *
 * One module, one function. Call sites in:
 *   - state/runState.ts addToParty (party member ids)
 *   - state/draftRoll.ts rollDraftOffer (offer ids — transfer to
 *     PartyMember.id on pick, persist forever in pokedex.skipped on
 *     pass)
 */

const HEX = '0123456789abcdef';

/** Format a 16-byte buffer as a uuid v4 string with the version + variant
 *  nibbles set per RFC 4122 section 4.4. */
function formatV4(b: Uint8Array): string {
  // byte 6: version = 4 → top nibble = 0100
  b[6] = (b[6]! & 0x0f) | 0x40;
  // byte 8: variant = 10xx → top two bits
  b[8] = (b[8]! & 0x3f) | 0x80;

  let s = '';
  for (let i = 0; i < 16; i++) {
    const v = b[i]!;
    s += HEX[v >> 4]! + HEX[v & 0x0f]!;
    if (i === 3 || i === 5 || i === 7 || i === 9) s += '-';
  }
  return s;
}

export function randomUUID(): string {
  // Prefer the native path when available — it's a single C++ call
  // vs the JS-loop fallback.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: crypto.getRandomValues IS available in insecure contexts.
  // Defensive: if even that's missing (very old browsers, no crypto at
  // all), fall through to Math.random — collision risk acceptable for
  // a friend-group game where party-member ids never need to be
  // cryptographically unique.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return formatV4(bytes);
}
